import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowRight,
  faCheck,
  faClose,
  faExternalLinkAlt,
  faKey,
  faPlugCircleBolt,
  faRotate,
} from "@fortawesome/free-solid-svg-icons"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import { ButtonIcon } from "../../../components/Button/Button"
import InputField from "../../../components/InputField/InputField"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { useRespostaAiIntegration } from "../../../hooks/RespostaAi/useRespostaAiIntegration"
import type { IRespostaAiInstance } from "../../../Objects/RespostaAi/IRespostaAiInstance"
import type { IRespostaAiSettings } from "../../../Objects/RespostaAi/IRespostaAiSettings"

const DEFAULT_RESPOSTA_AI_BASE_URL = "https://respostaai.com.br"

const RESPOSTA_AI_TRANSPORT = 1

type RespostaAiSetupStep = "key" | "instance"

interface RespostaAiSetupPopupProps {
  isOpen: boolean
  settings: IRespostaAiSettings | null
  onSettingsChange: (settings: IRespostaAiSettings) => void
  onClose: () => void
}

export const RespostaAiSetupPopup: React.FC<RespostaAiSetupPopupProps> = ({
  isOpen,
  settings,
  onSettingsChange,
  onClose,
}) => {
  const { t } = useTranslation(["pages", "helpPopup"])

  const { saveGlobalKey, listInstances, provision, activate } = useRespostaAiIntegration()

  const [step, setStep] = useState<RespostaAiSetupStep>("key")

  const [globalApiKey, setGlobalApiKey] = useState("")

  const [instances, setInstances] = useState<IRespostaAiInstance[]>([])

  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    setStep(settings?.hasGlobalApiKey ? "instance" : "key")

    setGlobalApiKey("")

    setSelectedInstanceId(settings?.instanceId ?? null)

    setInstances([])

    if (settings?.hasGlobalApiKey) {
      void handleLoadInstances()
    }
  }, [isOpen, settings?.hasGlobalApiKey, settings?.instanceId])

  if (!isOpen) {
    return null
  }

  const handleSaveGlobalKey = async () => {
    const current = await saveGlobalKey({
      baseUrl: DEFAULT_RESPOSTA_AI_BASE_URL,
      globalApiKey: globalApiKey.trim(),
    })

    onSettingsChange(current)

    setGlobalApiKey("")

    setStep("instance")

    await handleLoadInstances()
  }

  const handleLoadInstances = async () => {
    const nextInstances = await listInstances()

    setInstances(nextInstances)

    if (!selectedInstanceId && nextInstances.length === 1) {
      setSelectedInstanceId(nextInstances[0].id)
    }
  }

  const handleProvisionAndActivate = async () => {
    if (!selectedInstanceId) {
      return
    }

    const provisioned = await provision(selectedInstanceId)

    const activated = await activate(RESPOSTA_AI_TRANSPORT)

    onSettingsChange({ ...provisioned, ...activated })

    onClose()
  }

  const instanceOptions = instances.map((instance) => ({
    label: `${instance.name} • ${instance.status}`,
    value: instance.id,
  }))

  const stepIndex = step === "key" ? 1 : 2

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("label_resposta_ai_setup_title")}
              iconColor={THEME.colors.iconBGColor}
              icon={faPlugCircleBolt}
            >
              <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                <PopupHelpButton
                  variant="global"
                  helpKey="pages.resposta-ai-integration"
                  title={t("label_resposta_ai_title")}
                  description={t("label_resposta_ai_subtitle")}
                  bullets={t("helpPopup:global.respostaAiIntegration.bullets", { returnObjects: true }) as string[]}
                />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  ariaLabel={t("label_resposta_ai_close_setup")}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.respostaAiIntegration.respostaAiSetupPopup.button.onClose"
                />
              </div>
            </TitlePageResponsiveIconSmall>

            <div className="max-h-[72vh] overflow-y-auto p-2">
              <div className="mb-3 grid grid-cols-2 gap-2 text-center text-[11px] font-black text-slate-600">
                {["label_resposta_ai_setup_step_key", "label_resposta_ai_setup_step_instance"].map((key, index) => (
                  <span
                    key={key}
                    className={`rounded-lg border px-2 py-2 ${
                      index + 1 <= stepIndex
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : "border-slate-200 bg-slate-50 text-slate-500"
                    }`}
                  >
                    {t(key)}
                  </span>
                ))}
              </div>

              {step === "key" && (
                <section className="rounded-lg border-2 border-slate-200 bg-slate-50 p-3">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faKey} className="text-sky-700" />

                    <strong className="text-sm text-slate-900">{t("label_resposta_ai_step_key_title")}</strong>
                  </div>

                  <p className="mt-2 text-xs leading-relaxed text-slate-600">
                    {t("label_resposta_ai_step_key_description")}
                  </p>

                  <a
                    href={DEFAULT_RESPOSTA_AI_BASE_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.respostaAiIntegration.respostaAiSetupPopup.link.createAccount"
                    className="mt-3 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-lg border-2 border-sky-200 bg-white px-3 py-2 text-sm font-bold text-sky-700 shadow-sm hover:bg-sky-50 sm:w-auto"
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />

                    {t("label_resposta_ai_create_account")}
                  </a>

                  <div className="mt-3">
                    <InputField
                      type="password"
                      value={globalApiKey}
                      onChange={(event) => setGlobalApiKey(event.target.value)}
                      placeholder={t("label_resposta_ai_global_key")}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.respostaAiIntegration.respostaAiSetupPopup.input.globalApiKey"
                    />
                  </div>

                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => void handleSaveGlobalKey()}
                      disabled={!globalApiKey.trim()}
                      data-interaction-description-key="interactionDescriptions:pages.myRestaurant.respostaAiIntegration.respostaAiSetupPopup.button.confirmKey"
                      className="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-black text-white shadow-sm hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300 sm:w-auto"
                    >
                      <FontAwesomeIcon icon={faArrowRight} />

                      {t("label_resposta_ai_confirm_key")}
                    </button>
                  </div>
                </section>
              )}

              {step === "instance" && (
                <section className="rounded-lg border-2 border-slate-200 bg-white p-3">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faPlugCircleBolt} className="text-emerald-700" />

                    <strong className="text-sm text-slate-900">{t("label_resposta_ai_step_instance_title")}</strong>
                  </div>

                  <p className="mt-2 text-xs leading-relaxed text-slate-600">
                    {t("label_resposta_ai_step_instance_description")}
                  </p>

                  <div className="mt-3 flex items-end gap-2">
                    <div className="min-w-0 flex-1">
                      {instanceOptions.length > 0 ? (
                        <GenericDropDown
                          legend={t("label_resposta_ai_instance_select_label")}
                          data={instanceOptions}
                          initialState={selectedInstanceId}
                          handlerFuncion={(event) => setSelectedInstanceId(event.target.value)}
                          descriptionKey="interactionDescriptions:pages.myRestaurant.respostaAiIntegration.respostaAiSetupPopup.select.instance"
                        />
                      ) : (
                        <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 p-4 text-center text-xs text-slate-500">
                          {t("label_resposta_ai_instances_empty")}
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => void handleLoadInstances()}
                      data-interaction-description-key="interactionDescriptions:pages.myRestaurant.respostaAiIntegration.respostaAiSetupPopup.button.listInstances"
                      className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-lg border-2 border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm hover:bg-slate-50"
                    >
                      <FontAwesomeIcon icon={faRotate} />
                      {t("label_resposta_ai_list_instances")}
                    </button>
                  </div>

                  <div className="mt-3 flex flex-col gap-2 border-t border-slate-200 pt-3 sm:flex-row sm:justify-between">
                    <button
                      type="button"
                      onClick={() => setStep("key")}
                      data-interaction-description-key="interactionDescriptions:pages.myRestaurant.respostaAiIntegration.respostaAiSetupPopup.button.changeKey"
                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border-2 border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm hover:bg-slate-50"
                    >
                      <FontAwesomeIcon icon={faKey} />
                      {t("label_resposta_ai_change_key")}
                    </button>

                    <button
                      type="button"
                      onClick={() => void handleProvisionAndActivate()}
                      disabled={!selectedInstanceId}
                      data-interaction-description-key="interactionDescriptions:pages.myRestaurant.respostaAiIntegration.respostaAiSetupPopup.button.provision"
                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-black text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                    >
                      <FontAwesomeIcon icon={faCheck} />
                      {t("label_resposta_ai_provision")}
                    </button>
                  </div>
                </section>
              )}
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
